// Member-facing benefits: what I hold, and choosing where to claim it.
// The club director's side lives under `club`, not here — different person, different question.
//
// One vocabulary throughout: a member CLAIMS a benefit, which reserves it at one place, and the
// place USES it when they turn up. Nothing here says "redeem" at a member, because the server's
// `REDEEMED` state is the target's action and reads to a member as "already used".

const benefits = {
  navTitle: 'My Benefits',
  title: 'My Benefits',
  subtitle: 'Rewards you can claim at a participating club or coach.',

  aBenefit: 'your benefit',
  available: 'Ready to claim',
  claimed: 'Claimed',
  awaitingTarget: 'Waiting to be accepted',
  redeemed: 'Used',
  expired: 'Expired',
  expiresOn: 'Claim by {{date}}',
  claimedAt: 'Claimed at {{targetName}}',

  // Partners where an unclaimed benefit can be used. Never claimed in the app — the member follows
  // the partner's link, and the partner claims it on their side.
  partnersHeading: 'Use it with a partner',
  useAtPartner: 'Use at {{partnerName}}',

  // Headings for the three groups the list is split into. The count is appended by the component,
  // so these are nouns rather than sentences.
  sectionClaimable: 'Ready to claim',
  sectionClaimed: 'Claimed',
  sectionHistory: 'History',

  emptyTitle: "You don't have any benefits yet",
  emptyDesc: 'Benefits arrive when you subscribe or renew.',

  // Failure, kept separate from every empty state below. "You have none" and "we could not find
  // out" are different claims, and saying the first when we mean the second tells a member who
  // holds an unclaimed credit that they hold nothing.
  loadFailedTitle: "We couldn't load your benefits",
  loadFailedDesc: 'Something went wrong on our end. Anything you hold is still yours — try again.',
  tryAgain: 'Try again',

  // --- Choosing where to claim -----------------------------------------------------------------
  claimCta: 'Choose where to claim',
  claimCtaDisabled: 'Select where to claim',
  claimTitle: 'Claim {{label}}',
  claimAt: 'Claim at {{targetName}}',
  claimAtClub: 'Claim your benefit here',
  claimPickDesc: 'Pick where you want to use it. You can claim in one place.',
  claimAtTargetDesc: 'This will claim your benefit at {{targetName}}.',
  // Stated before the button, not after: DUPR cannot move a claim once made, and a member should
  // know that while they still have the choice.
  claimIsFinal: "Once claimed, only that club or coach can honor it — DUPR can't move it afterwards.",
  claimFailed: "That didn't go through. Please try again.",

  targetKindLabel: 'Where to claim',
  tabClubs: 'Clubs',
  tabCoaches: 'Coaches',
  searchClubs: 'Search participating clubs',
  searchCoaches: 'Search participating coaches',
  viewClub: 'View club',
  viewCoach: 'View coach',
  loadMoreClubs: 'Show more clubs',
  loadMoreCoaches: 'Show more coaches',

  // --- Confirming ------------------------------------------------------------------------------
  // A separate step because claiming cannot be undone. The sentence below is the whole reason it
  // exists, so it says what becomes true rather than asking "are you sure?".
  confirmTitle: 'Claim {{label}} at {{targetName}}?',
  confirmDesc: 'Check this is where you want it before you claim.',
  // Finality, and — for a target that has to accept — what happens to the claim next. Deliberately
  // NOT how the benefit is used: that is the definition's to say and the server's to resolve, it
  // arrives on `redemption` once there is a target to name, and `RedemptionSteps` renders it. The
  // front-desk sentence that used to live here was in-person instructions hard-coded at the one
  // moment the client cannot know the method, so it was wrong for a scan or online benefit.
  confirmClubBody: "Your benefit will be tied to {{targetName}} and can't be moved later.",
  confirmCoachBody:
    "Your benefit will be tied to {{targetName}} and can't be moved later. They'll get a request to accept it.",
  confirmCta: 'Confirm claim',
  chooseSomewhereElse: 'Choose somewhere else',

  // --- After claiming --------------------------------------------------------------------------
  pendingStepDesc: "{{targetName}} has your claim and hasn't accepted it yet. We'll let you know when they do.",
  // How a claimed benefit is used. The server decides WHICH of these lines appear and in what
  // order — a benefit now says how it is redeemed — and sends each one's key alongside its own
  // English. These are our translations of the keys it ships by default; a phrase an admin typed
  // has no key and arrives already written.
  //
  // The names are the server's, verbatim and namespace-free (`RedemptionMethod.KEY_*`), with
  // `benefits.` added by `redemptionText`. Renaming one here silently drops back to the server's
  // wording. The numbers are gone because the list is an `<ol>` now, which is what a screen reader
  // needed all along.
  // Row headings inside the identity block a member shows at the counter. The server names
  // these keys; `worthLabel` is ours alone, because the amount is formatted by this app
  // rather than sent as a field.
  redemptionIdentityLabel: 'Show this at the desk',
  redemptionScanLabel: 'Scan to redeem',
  redemptionFieldName: 'Name',
  redemptionFieldDuprId: 'DUPR ID',
  redemptionFieldReference: 'Claim ref',
  worthLabel: 'Worth',
  redemptionInPersonTitle: 'Using your benefit',
  redemptionInPersonVisit: 'Visit {{targetName}}.',
  redemptionInPersonShow: 'Show this claim.',
  redemptionInPersonApply: 'They apply it — nothing is spent in the app.',
  redemptionInPersonFootnote:
    "Claiming reserved this for you — it isn't used until {{targetName}} applies it in person.",
  redemptionOnlineTitle: 'Using your benefit',
  redemptionOnlineOpen: 'Open the link below.',
  redemptionOnlineFinish: 'Your benefit is applied when you check out.',
  redemptionOnlineLinkLabel: 'Redeem online',

  // --- Empty and failed target lists -------------------------------------------------------------
  noTargetsTitle: 'Nowhere to claim this yet',
  noTargetsDesc:
    'No club or coach near you is taking this benefit right now. New ones join all the time, and your benefit stays yours until you claim it.',

  // The same distinction as above, and it matters more here: the empty state reads as "your benefit
  // is worth nothing anywhere", which is a bad thing to say because a request failed.
  targetsFailedTitle: "We couldn't load anywhere to claim",
  targetsFailedDesc: 'Something went wrong on our end. Your benefit is still yours — try again.',
  // A benefit that names nowhere it can be claimed in the app. Not normally reachable — such a
  // benefit is not listed — but a link can still name one.
  nothingToClaim: "This benefit can't be claimed here.",

  // --- A bundle made of several benefits ---------------------------------------------------------
  // A tab for a part that can go to more than one kind of place: "1 hour lesson (Coach)".
  partAtKind: '{{label}} ({{kind}})',
  partAvailable: 'Ready to claim',
  partClaimed: 'Claimed',
  partReserved: 'In use at a partner',
  partUsed: 'Used',
  // Another choice in the same group was taken.
  partLocked: 'Not chosen',
  partExpired: 'Expired',

  loading: 'Loading…',
  selected: 'Selected',
  close: 'Close',

  // Club browse filter. Describes the CLUB, not the member: it asks which clubs take DUPR+
  // Benefits at all, and answers the same way whoever is asking. The old wording promised a
  // caller-relative list and delivered one — clubs the member could not use vanished, with no way
  // to tell that from a club taking nothing at all.
  filterParticipating: 'Only show clubs accepting DUPR+ Benefits',

  // The mark on a participating club, and what it opens.
  clubBenefitBadge: 'DUPR+',
  // Spoken to screen readers, where the count is worth having; the badge itself stays short.
  clubAcceptsBenefits_one: 'Accepts {{count}} DUPR+ Benefit',
  clubAcceptsBenefits_other: 'Accepts {{count}} DUPR+ Benefits',
  clubBenefitsTitle: 'DUPR+ Benefits',
  clubBenefitsEmpty: 'This club isn\u2019t offering DUPR+ Benefits right now.',
  clubBenefitsError: 'Couldn\u2019t load this club\u2019s DUPR+ Benefits.',
  // Precise rather than a verdict: all a client can tell is that the member is not holding this
  // one. Whether the club would accept them is the server's to answer, and it never discloses it —
  // "Not eligible" would put words in its mouth.
  clubNotEligible: 'Not one of your benefits',
}

export default benefits
